'use client';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CalendarIcon, Clock, AlertTriangle, X } from 'lucide-react';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { cn } from '~/lib/utils';
import { format } from 'date-fns';
import { useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useShallow } from 'zustand/react/shallow';
import { useUserStore } from '~/stores/user-store';
import { de } from 'date-fns/locale';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { trpc } from '~/api/trpc';
import LocalDateTime from '~/lib/localdatetime';
// Updated Time Input Component using HTML5 time input
type TimeInputProps = {
    value: string; // Expected to be in HH:MM format
    onChange: (value: string) => void; // Will receive HH:MM format
    placeholder?: string;
    disabled?: boolean;
};

const TimeInput = ({
    value,
    onChange,
    placeholder,
    disabled = false,
}: TimeInputProps) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if(!e.target.value || e.target.value.trim().length === 0) {
            return;
        }
        onChange(e.target.value);
    };

    return (
        <div className="relative">
            <Input
                type="time"
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
                className={cn(
                    disabled && "bg-muted cursor-not-allowed opacity-70"
                )}
            />
        </div>
    );
};

type DateTimeSelectorProps = {
    onOpenSlotFinder?: () => void;
};

export const DateTimeSelector = ({ onOpenSlotFinder }: DateTimeSelectorProps) => {
    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
        }))
    );
    const {
        steps,
        startDate, setStartDate,
        startTime, setStartTime,
        refreshSelectableResources
    } = useCreateOrModifyAppointmentStore(
        useShallow(state => ({
            steps: state.steps,
            startDate: state.startDate,
            setStartDate: state.setStartDate,
            startTime: state.startTime,
            setStartTime: state.setStartTime,
            refreshSelectableResources: state.refreshSelectableResources,
        }))
    );

    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
        }))
    );

    const trpcUtils = trpc.useUtils();

    // State für manuelle Eingabe
    const [isManualInputEnabled, setIsManualInputEnabled] = useState(false);
    const [showWarningDialog, setShowWarningDialog] = useState(false);

    // Calculate end date and time based on steps
    const endDateTime = useMemo(() => {
        if (steps.length === 0) return LocalDateTime.fromUTCDate(new Date(), 'Europe/Berlin');

        const totalMinutes = steps.reduce((max, step) => Math.max(max, step.minuteEnd), 0);
        let endDate = startDate.clone();

        // Parse start time
        const [hours, minutes] = startTime.split(':').map(Number);

        // Set the start time on the date
        endDate = endDate.setHours(hours).setMinutes(minutes).addMinutes(totalMinutes)

        // Add the total minutes to get the end time

        return endDate;
    }, [steps, startDate, startTime]);

    // Format end time for display
    const formattedEndTime = useMemo(() => {
        const hours = endDateTime.getHours().toString().padStart(2, '0');
        const minutes = endDateTime.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }, [endDateTime]);

    const handleTimeChange = (newTime: string) => {
        console.log("newTime", newTime);
        setStartTime(newTime);
        if (selectedStore?.id) {
            refreshSelectableResources(selectedStore.id, timezone, trpcUtils);
        }
    };

    const handleManualInputToggle = () => {
        if (!isManualInputEnabled) {
            // User wants to enable manual input - show warning
            setShowWarningDialog(true);
        } else {
            // User wants to disable manual input - just do it
            setIsManualInputEnabled(false);
        }
    };

    const handleConfirmManualInput = () => {
        setIsManualInputEnabled(true);
        setShowWarningDialog(false);
    };

    if (steps.length === 0 || !endDateTime) {
        return null;
    }

    return (
        <>
            <div className='flex flex-col gap-4'>
                {/* Button für manuelle Eingabe - oberhalb der Grid */}
                <div className='flex justify-end'>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleManualInputToggle}
                        className={cn(
                            "h-8 text-xs gap-1.5",
                            !isManualInputEnabled && "bg-grey-100 text-grey-900 border-grey-400 hover:bg-grey-200 hover:text-grey-900"
                        )}
                    >
                        {!isManualInputEnabled && <AlertTriangle className="h-3.5 w-3.5" />}
                        {isManualInputEnabled ? 'Manuelle Eingabe deaktivieren' : 'Manuelle Eingabe aktivieren'}
                    </Button>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                        <Label>Start</Label>
                        <div className='flex flex-col space-y-2'>
                            <Popover>
                                <PopoverTrigger asChild disabled={!isManualInputEnabled}>
                                    <Button
                                        variant={'outline'}
                                        className={cn(
                                            'w-full justify-start text-left font-normal',
                                            !isManualInputEnabled && 'bg-muted cursor-not-allowed opacity-70'
                                        )}
                                        disabled={!isManualInputEnabled}
                                    >
                                        <CalendarIcon className='mr-2 h-4 w-4' />
                                        {startDate.format('dd. MMMM yyyy')}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className='w-auto p-0' align='start'>
                                    <Calendar
                                        mode='single'
                                        locale={de}
                                        selected={startDate.toTimezonedDate()}
                                        onSelect={(date) => {
                                            if (!date) {
                                                return;
                                            }
                                            setStartDate(LocalDateTime.fromLocalDate(date, startDate.getTimezone()))
                                            if (selectedStore?.id) {
                                                refreshSelectableResources(selectedStore.id, timezone, trpcUtils);
                                            }
                                        }}
                                        numberOfMonths={1}
                                        weekStartsOn={1}
                                    />
                                </PopoverContent>
                            </Popover>

                            <TimeInput
                                value={startTime}
                                onChange={handleTimeChange}
                                placeholder="09:00"
                                disabled={!isManualInputEnabled}
                            />
                        </div>
                    </div>

                    <div className='space-y-2'>
                        <Label>Ende</Label>
                        <div className='flex flex-col space-y-2'>
                            <Popover>
                                <PopoverTrigger asChild disabled>
                                    <Button
                                        variant={'outline'}
                                        className={cn('w-full justify-start text-left font-normal bg-muted cursor-not-allowed opacity-70')}
                                        disabled
                                    >
                                        <CalendarIcon className='mr-2 h-4 w-4' />
                                        {endDateTime.format('dd. MMMM yyyy')}
                                    </Button>
                                </PopoverTrigger>
                            </Popover>

                            <TimeInput
                                value={formattedEndTime}
                                onChange={() => {}} // Read-only
                                disabled={true}
                            />
                        </div>
                    </div>
                </div>
                <span>
                    Du buchst einen Termin für diese Zeitzone: <b>{timezone || 'Europe/Berlin'}</b>
                </span>
            </div>

            {/* Warning Dialog */}
            <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
                            <AlertDialogContent className="sm:max-w-[500px]">
                                <button
                                    onClick={() => setShowWarningDialog(false)}
                                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
                                >
                                    <X className="h-4 w-4" />
                                    <span className="sr-only">Close</span>
                                </button>
                                <AlertDialogHeader>
                                    <AlertDialogTitle className="flex items-center gap-2">
                                        <AlertTriangle className="h-5 w-5 text-yellow-500" />
                                        Manuelle Bearbeitung
                                    </AlertDialogTitle>
                                    <AlertDialogDescription className="text-base pt-4 pb-6">
                                        Um einen optimalen Termin zu finden, nutze am besten den Terminfinder.
                                        <br /><br />
                                        Wenn du den Termin trotzdem manuell bearbeiten möchtest, stelle bitte sicher, dass dabei keine Ressourcenkonflikte entstehen.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-row justify-between gap-3">
                                    <AlertDialogAction
                                        onClick={handleConfirmManualInput}
                                        className="bg-red-700 hover:bg-red-800 text-white"
                                    >
                                        <AlertTriangle className="h-4 w-4 mr-2" />
                                        Trotzdem fortfahren
                                    </AlertDialogAction>
                                    {onOpenSlotFinder && (
                                        <AlertDialogAction
                                            onClick={() => {
                                                setShowWarningDialog(false);
                                                onOpenSlotFinder();
                                            }}
                                            className="bg-green-700 hover:bg-green-800 text-white"
                                        >
                                            <CalendarIcon className="h-4 w-4 mr-2" />
                                            Zum Terminfinder
                                        </AlertDialogAction>
                                    )}
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
        </>
    );
};